import moment from "moment";

import { RecipientDetailsModel } from "@/Appointments/Models/Appointment/RecipientDetailsModel";
import { Client } from "@/Common/Api/Client";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { displayToast, MessageToast } from "@/Common/Components/Toast/Toast";
import { BillingPaymentMethodsHelper } from "@/Common/Helpers/BillingPaymentMethodsHelper";
import { CommonHelper } from "@/Common/Helpers/CommonHelper";
import { DateTime } from "@/Common/Helpers/DateTime";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { EmployeeTitle } from "@/Common/Models/Employee/Enums/EmployeeTitle";
import { EmployeeType } from "@/Common/Models/Employee/Enums/EmployeeType";
import { Guid } from "@/Common/Models/Guid";
import { Person } from "@/Common/Models/Person/Person";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { SharedItemPriority } from "@/Common/Models/Portal/SharedItem/SharedItemPriority";
import { SitesModel } from "@/Common/Models/Sites/SitesModel";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { BillingPaymentMethod } from "@/Common/Models/UI/BillingPaymentMethod";
import { PrivateBill_BilledTo } from "@/Common/Models/UI/Enums";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { UpsertAndSyncBillToPortalResponse } from "@/Patient/Api/History/Dtos/UpsertAndSyncBillToPortalResponse";
import { BuildSavePrivateBillRequestHelper } from "@/Patient/Helpers/BuildSavePrivateBillRequestHelper";
import { DiscountType } from "@/Patient/Models/Billing/Enum/DiscountType";
import { PatientPrivateBillItem } from "@/Patient/Models/History/PatientPrivateBillItem";
import { PatientPrivateBillListItem } from "@/Patient/Models/History/PatientPrivateBillListItem";
import { BillableServiceDropdownItem } from "@/Patient/Models/UI/BillingHistory/BillableServiceDropdownItem";
import { IBillableService } from "@/Patient/Models/UI/History/IBillableService";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class PatientHistoryPrivateBillHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_CriteriaClick, this.CriteriaClick);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_LoadPatientPrivateBill, this.LoadPatientPrivateBill);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_ShowSelectedPrivateBill, this.ShowSelectedPrivateBill);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_FromDateChanged, this.FromDateChanged);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_ToDateChanged, this.ToDateChanged);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_SearchText, this.SearchText);

        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_Print, this.PrintPrivateBill);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_CloseInvalidPopup, this.CloseInvalidPopup);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_InvalidateBill, this.InvalidateBill);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_NoteInvalidationChange, this.NoteInvalidationChange);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_SaveInvalidation, this.SaveInvalidation);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_DateOfVisit_Set, this.DateOfVisitChange);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_CreateNewBill, this.CreateNewBill);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_OnTodayClick, this.OnTodayClick);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_SiteChange, this.OnSiteChanged);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_AddNewEmptyService, this.AddNewEmptyService);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_RemoveService, this.RemoveService);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_ChangeHasGST, this.ChangeHasGST);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_ChangeHasQST, this.ChangeHasQST);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_ChangeHasHST, this.ChangeHasHST);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_ChangeHasGenericTax, this.ChangeHasGenericTax);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_CancelNewBill, this.CancelNewBill);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_OnCancelClick, this.OnCancelClick);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_CloseCancelPopup, this.CloseCancelPopup);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_SelectBillableService, this.SelectBillableService, false);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_OnQuantityChange, this.OnQuantityChange);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_OnPriceChange, this.OnPriceChange);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_ClearSelection, this.ClearSelection);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_OnDiscountChange, this.OnDiscountChange);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_ChangeDiscountType, this.ChangeDiscountType);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_OnSelectedPaymentChange, this.OnSelectedPaymentChange);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_SelectClinician, this.SelectClinician);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_RemoveSelectedClinician, this.RemoveSelectedClinician);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_OnNotesChange, this.OnNotesChange);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_OnCostCenterChange, this.OnCostCenterChange);

        this.RegisterOperation(LocalPatientOperations.History.LoadDummyServices, this.LoadDummyServices);

        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_OnSaveClick, this.OnSaveClick);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_OnFaxClick, this.OnFaxClick);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_OnPrintClick, this.OnPrintClick);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_OnSaveAndPrintClick, this.OnSaveAndPrintClick);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_PortalStatusClicked, this.PortalStatusClicked);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_PortalStatusClicked_FromEdit, this.PortalStatusClicked);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_PortalStatusCancel, this.PortalStatusCancel);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_PortalStatusRevoke, this.privateBillPortalStatusRevoked);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_PortalStatusSaveFromEdit, this.PortalStatusSaveFromEdit);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_PortalStatusSaveAndPrintFromEdit, this.PortalStatusSaveAndPrintFromEdit);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_DeleteSelectedBill, this.DeleteSelectedBill);

        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_CloseSelectedBill, this.closeSelectedBill);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_CloseSelectedBillThenPrint, this.closeSelectedBillThenPrint);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_SendBillToPortal, this.sendBillToPortal);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_SendBillToPortalThenPrint, this.sendBillToPortalThenPrint);

        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_OnModifyOrDuplicateClick, this.OnModifyOrDuplicateClick);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_LockProvider, this.LockProvider);
        this.RegisterOperation(LocalPatientOperations.History.OnPortalInvoiceMessageChecked, this.onPortalInvoiceMessageChecked);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_OnRecipientDetailsChange, this.onRecipientDetailsChanged);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_OpenMarkBillAsPaidPopup, this.openMarkBillAsPaidPopup);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_CloseMarkBillAsPaidPopup, this.closeMarkBillAsPaidPopup);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_UpdateBillAndCloseMarkBillAsPaidPopup, this.updateBillAndCloseMarkBillAsPaidPopup);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_Billing_OnBilledToChange, this.onBilledToChanged);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_Billing_SaveAndSyncNewBillToPortal, this.UpsertAndSyncBillToPortal);
        this.RegisterOperation(LocalPatientOperations.History.PrivateBill_CloseSendToPortalPopup, this.closeSendToPortalPopup);
    }

    private CriteriaClick(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        var open = privateBillModel.get("IsCriteriaExpanded");

        privateBillModel = privateBillModel.set("IsCriteriaExpanded", !open);

        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
    }

    private LoadPatientPrivateBill(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];

        if (privateBillModel.get("IsCriteriaExpanded"))
            store.ActionsHistory.LoadPatientPrivateBill(store.PatientModels[request.PatientId].InactivableResponse.Id, privateBillModel.get("SearchText"), privateBillModel.get("DateTo"), privateBillModel.get("DateFrom"));
        else
            store.ActionsHistory.LoadPatientPrivateBill(store.PatientModels[request.PatientId].InactivableResponse.Id, privateBillModel.get("SearchText"), null, null);
    }

    private ShowSelectedPrivateBill(request: LocalEvent, store: PatientStore): void {
        const privateBillId = request.Get<PatientPrivateBillListItem>("privateBill").Model.PrivateBillId;
        store.ActionsHistory.LoadPrivateBillInfo(privateBillId);
    }

    private FromDateChanged(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        privateBillModel = privateBillModel.set("DateFrom", request.Get<Date>("value"));

        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;

        store.ActionsHistory.LoadPatientPrivateBill(store.PatientModels[request.PatientId].InactivableResponse.Id, privateBillModel.get("SearchText"), privateBillModel.get("DateFrom"), privateBillModel.get("DateTo"));
    }

    private ToDateChanged(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        privateBillModel = privateBillModel.set("DateTo", request.Get<Date>("value"));
        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
        store.ActionsHistory.LoadPatientPrivateBill(store.PatientModels[request.PatientId].InactivableResponse.Id, privateBillModel.get("SearchText"), privateBillModel.get("DateFrom"), privateBillModel.get("DateTo"));
    }

    private SearchText(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        var searchText = request.Get<string>("value");

        privateBillModel = privateBillModel.set("SearchText", searchText);
        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;

        if (privateBillModel.get("IsCriteriaExpanded") === true)
            store.ActionsHistory.LoadPatientPrivateBill(store.PatientModels[request.PatientId].InactivableResponse.Id, searchText, privateBillModel.get("DateFrom"), privateBillModel.get("DateTo"));
        else
            store.ActionsHistory.LoadPatientPrivateBill(store.PatientModels[request.PatientId].InactivableResponse.Id, searchText, null, null);
    }

    private PrintPrivateBill(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
        store.ActionsHistory.PrintPrivateBill(privateBillModel.get("SelectedPrivateBillId"));
    }

    private CloseInvalidPopup(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        privateBillModel = privateBillModel.set("IsInvalidPopupOpen", false);
        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
        sessionStore.hideGlobalOverlay();
    }

    private InvalidateBill(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];

        privateBillModel = privateBillModel.set("IsInvalidPopupOpen", true);
        privateBillModel = privateBillModel.set("InvalidationNoteForSave", "");
        sessionStore.showGlobalOverlay(true);
        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
    }

    private NoteInvalidationChange(request: LocalEvent, store: PatientStore): void {
        var val = request.Get<string>("value");
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        privateBillModel = privateBillModel.set("InvalidationNoteForSave", val);
        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
    }

    private SaveInvalidation(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        store.ActionsHistory.InvalidatePrivateBill(privateBillModel.get("PrivateBillId"), privateBillModel.get("InvalidationNoteForSave"));
        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
        sessionStore.hideGlobalOverlay();
    }

    private OnCostCenterChange(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        privateBillModel = privateBillModel.set("SelectedCostCenterId", request.Get<string>("value"));

        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
    }

    private OnSaveAndPrintClick(request: LocalEvent, store: PatientStore): void {
        var isFromClose = request.Get<boolean>("IsFromClose");
        this.SaveAndPrint(request, store, false, isFromClose);
    }

    private OnNotesChange(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        privateBillModel = privateBillModel.set("NewBillNotes", request.Get<string>("value"));
        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
    }

    private OnSaveClick(request: LocalEvent, store: PatientStore): void {
        var isFromClose = request.Get<boolean>("IsFromClose");
        this.Save(request, store, false, isFromClose);
    }

    private Save(request: LocalEvent, store: PatientStore, isInPortalSyncWorkFlow: boolean = false, isFromClose: boolean = false): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        var billedServices = store.PatientHistoryBillableServicesModels[request.PatientId];
        var createdById = sessionStore.getCurrentEmployee().ContactFacetId;
        privateBillModel = privateBillModel.set("IsSave", true);
        privateBillModel = privateBillModel.set("IsSaveLocked", true);

        var portalStatusModel = privateBillModel.get("PortalLinkedItemModel");
        portalStatusModel = portalStatusModel.set("NewMessageText", request.Get<string>("messageText"));
        privateBillModel = privateBillModel.set("PortalLinkedItemModel", portalStatusModel);

        sessionStore.cachedPrivateBillSiteId = privateBillModel.get("SelectedSiteId");

        var costCenterList = privateBillModel.get("CostCenterList");
        if (costCenterList != null && costCenterList.count() > 0) {
            sessionStore.cachedPrivateBillCostCenterId = privateBillModel.get("SelectedCostCenterId");
        }
        else {
            sessionStore.cachedPrivateBillCostCenterId = "";
        }

        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;

        // Remove Empty Line
        billedServices = billedServices.filter(x =>
            !IsNullOrWhiteSpace(x.get("NameFr")) ||
            !IsNullOrWhiteSpace(x.get("NameEn")) ||
            !IsNullOrWhiteSpace(x.get("NameEs")) ||
            x.get("IsFromServiceList") == true ||
            (!isNaN(x.get("Price")) && x.get("Price") != 0)
        );

        if (!isInPortalSyncWorkFlow)
            store.ActionsHistory.SavePrivateBill(privateBillModel, billedServices, store.PatientModels[request.PatientId].InactivableResponse.Id, createdById, isFromClose ? isFromClose : false, false);
        if (isInPortalSyncWorkFlow)
            store.ActionsHistory.SavePrivateBill(privateBillModel, billedServices, store.PatientModels[request.PatientId].InactivableResponse.Id, createdById, true, true);
    }

    private SaveAndPrint(request: LocalEvent, store: PatientStore, isInPortalSyncWorkFlow: boolean = false, isFromClose: boolean = false): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        var billedServices = store.PatientHistoryBillableServicesModels[request.PatientId];
        var createdById = sessionStore.getCurrentEmployee().ContactFacetId;
        privateBillModel = privateBillModel.set("IsSave", false);
        privateBillModel = privateBillModel.set("IsSaveLocked", true);

        var portalStatusModel = privateBillModel.get("PortalLinkedItemModel");
        portalStatusModel = portalStatusModel.set("NewMessageText", request.Get<string>("messageText"));
        privateBillModel = privateBillModel.set("PortalLinkedItemModel", portalStatusModel);

        sessionStore.cachedPrivateBillSiteId = privateBillModel.get("SelectedSiteId");

        if (privateBillModel.get("CostCenterList").count() > 0) {
            sessionStore.cachedPrivateBillCostCenterId = privateBillModel.get("SelectedCostCenterId");
        }
        else {
            sessionStore.cachedPrivateBillCostCenterId = "";
        }

        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;

        // Remove Empty Line
        billedServices = billedServices.filter(x =>
            !IsNullOrWhiteSpace(x.get("NameFr")) ||
            !IsNullOrWhiteSpace(x.get("NameEn")) ||
            !IsNullOrWhiteSpace(x.get("NameEs")) ||
            x.get("IsFromServiceList") == true ||
            (!isNaN(x.get("Price")) && x.get("Price") != 0)
        );

        if (!isInPortalSyncWorkFlow)
            store.ActionsHistory.SavePrivateBill(privateBillModel, billedServices, store.PatientModels[request.PatientId].InactivableResponse.Id, createdById, isFromClose ? isFromClose : false, false);
        else
            store.ActionsHistory.SavePrivateBill(privateBillModel, billedServices, store.PatientModels[request.PatientId].InactivableResponse.Id, createdById, isFromClose ? isFromClose : false, true);
    }

    private RemoveSelectedClinician(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        privateBillModel = privateBillModel.set("SelectedClinicians", null);
        privateBillModel = privateBillModel.set("SelectedClinicianName", "");
        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
    }

    private SelectClinician(request: LocalEvent, store: PatientStore): void {
        let provider = request.Get<PersonDropdownItem>("value");

        if (provider == null || provider.Item == null)
            return;

        store.PatientHistoryBillsModels[request.PatientId] = store.handlePrivateBillProviderSelectionChange(store.PatientHistoryBillsModels[request.PatientId], provider.Item);
    }

    private OnSelectedPaymentChange(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        privateBillModel = privateBillModel.set("SelectedPaymentMethod", Number(request.Get("value")));
        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
    }

    private CalculateSubtotalAndTotal(patientId: string, store: PatientStore, billedService: IBillableService[]): void {
        let privateBillModel = store.PatientHistoryBillsModels[patientId];

        privateBillModel = privateBillModel.set("NewBillTotal", 0);
        privateBillModel = privateBillModel.set("NewBillSubtotal", 0);
        privateBillModel = privateBillModel.set("NewBillTotalAmount", 0);

        let total = 0;
        let subtotal = 0;
        let totalAmountWithoutDiscount = 0;
        let totalGst = 0;
        let totalQst = 0;
        let totalHst = 0;
        let totalGenericTax = 0;

        let gstPercent = privateBillModel.get("GSTPercent") / 100;
        let qstPercent = privateBillModel.get("QSTPercent") / 100;
        let hstPercent = privateBillModel.get("HSTPercent") / 100;
        let genericTaxPercent = privateBillModel.get("GenericTaxPercent") / 100;
        if (isNaN(gstPercent) || IsNullOrUndefined(gstPercent))
            gstPercent = 0;
        if (isNaN(qstPercent) || IsNullOrUndefined(qstPercent))
            qstPercent = 0;
        if (isNaN(hstPercent) || IsNullOrUndefined(hstPercent))
            hstPercent = 0;
        if (isNaN(genericTaxPercent) || IsNullOrUndefined(genericTaxPercent))
            genericTaxPercent = 0;

        let cashDiscountType = privateBillModel.get("IsCashSelected") === true ? DiscountType.Cash : DiscountType.Percent;

        // Fix Bad Qtys
        billedService = billedService.map(s => {
            let qty = s.get("Qty");
            if (isNaN(qty) || IsNullOrUndefined(qty))
                s = s.set("Qty", 1);

            return s;
        });

        // Calculate totalAmountWithoutDiscount
        billedService.forEach(s => {
            let servicePriceTotal = Number(s.get("Price")) * s.get("Qty");
            totalAmountWithoutDiscount += servicePriceTotal;
        });

        // Convert Cash Discount
        let discountPercentage = 0;
        if (cashDiscountType == DiscountType.Cash && privateBillModel.get("NewBillDiscount") > 0 && totalAmountWithoutDiscount > 0) {
            discountPercentage = privateBillModel.get("NewBillDiscount") / totalAmountWithoutDiscount;
            cashDiscountType = DiscountType.Percent;
        }
        else if (cashDiscountType == DiscountType.Percent && privateBillModel.get("NewBillDiscount") > 0)
            discountPercentage = privateBillModel.get("NewBillDiscount") / 100;

        // Set Tax and Apply Discount on taxable Item;
        billedService = billedService.map(s => {
            let servicePriceTotal = Number(s.get("Price")) * s.get("Qty");
            let serviceFinalPrice = servicePriceTotal;

            let serviceTax_Gst = (s.get("HasGST") === true) ? servicePriceTotal * gstPercent : 0;
            let serviceTax_Qst = (s.get("HasQST") === true) ? servicePriceTotal * qstPercent : 0;
            let serviceTax_Hst = (s.get("HasHST") === true) ? servicePriceTotal * hstPercent : 0;
            let serviceTax_GenericTax = (s.get("HasGenericTax") === true) ? servicePriceTotal * genericTaxPercent : 0;
            let serviceTaxFinal_Gst = serviceTax_Gst;
            let serviceTaxFinal_Qst = serviceTax_Qst;
            let serviceTaxFinal_Hst = serviceTax_Hst;
            let serviceTaxFinal_GenericTax = serviceTax_GenericTax;

            // Apply Discount
            if (discountPercentage > 0) {
                let percentageDiscounted = discountPercentage;
                serviceTaxFinal_Gst -= serviceTax_Gst * percentageDiscounted;
                serviceTaxFinal_Qst -= serviceTax_Qst * percentageDiscounted;
                serviceTaxFinal_Hst -= serviceTax_Hst * percentageDiscounted;
                serviceTaxFinal_GenericTax -= serviceTax_GenericTax * percentageDiscounted;
                serviceFinalPrice -= serviceFinalPrice * percentageDiscounted;
            }

            subtotal += serviceFinalPrice;
            totalGst += serviceTaxFinal_Gst;
            totalQst += serviceTaxFinal_Qst;
            totalHst += serviceTaxFinal_Hst;
            totalGenericTax += serviceTaxFinal_GenericTax;
            total += serviceFinalPrice + serviceTaxFinal_Gst + serviceTaxFinal_Qst + serviceTaxFinal_Hst + serviceTaxFinal_GenericTax;

            s = s.set("GST", serviceTax_Gst);
            s = s.set("QST", serviceTax_Qst);
            s = s.set("HST", serviceTax_Hst);
            s = s.set("GenericTax", serviceTax_GenericTax);

            return s;
        });

        // Round pennies....
        privateBillModel = privateBillModel.set("NewBillSubtotal", Math.round((subtotal + 0.00001) * 100) / 100);
        privateBillModel = privateBillModel.set("NewBillTotalAmount", Math.round((totalAmountWithoutDiscount + 0.00001) * 100) / 100);
        privateBillModel = privateBillModel.set("NewBillTotal", Math.round((total + 0.00001) * 100) / 100);
        privateBillModel = privateBillModel.set("NewBillGSTTotal", Math.round((totalGst + 0.00001) * 100) / 100);
        privateBillModel = privateBillModel.set("NewBillQSTTotal", Math.round((totalQst + 0.00001) * 100) / 100);
        privateBillModel = privateBillModel.set("NewBillHSTTotal", Math.round((totalHst + 0.00001) * 100) / 100);
        privateBillModel = privateBillModel.set("NewBillGenericTaxTotal", Math.round((totalGenericTax + 0.00001) * 100) / 100);

        store.PatientHistoryBillableServicesModels[patientId] = billedService;
        store.PatientHistoryBillsModels[patientId] = privateBillModel;
    }

    private ChangeDiscountType(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        var changeType = privateBillModel.get("IsCashSelected");
        var allServices = store.PatientHistoryBillableServicesModels[request.PatientId];

        privateBillModel = privateBillModel.set("IsCashSelected", !changeType);

        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;

        this.CalculateSubtotalAndTotal(request.PatientId, store, allServices);
    }

    private OnDiscountChange(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];

        var allServices = store.PatientHistoryBillableServicesModels[request.PatientId];

        privateBillModel = privateBillModel.set("NewBillDiscount", request.Get<number>("value"));

        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;

        this.CalculateSubtotalAndTotal(request.PatientId, store, allServices);
    }

    private ClearSelection(request: LocalEvent, store: PatientStore): void {
        var allServices = store.PatientHistoryBillableServicesModels[request.PatientId];
        var id = request.Get<string>("id");
        delete store.PatientHistoryBillableServicesModels[request.PatientId];

        function indexOf(services: IBillableService[], id: string): number {
            for (var i = 0; i < services.length; i++) {
                if (services[i].get("Id") === id)
                    return i;
            }
            return -1;
        }

        var serviceIndex = indexOf(allServices, id);

        if (serviceIndex >= 0) {
            allServices[serviceIndex] = allServices[serviceIndex].set("SelectedService", null);
            allServices[serviceIndex] = allServices[serviceIndex].set("HasGST", false);
            allServices[serviceIndex] = allServices[serviceIndex].set("HasQST", false);
            allServices[serviceIndex] = allServices[serviceIndex].set("HasHST", false);
            allServices[serviceIndex] = allServices[serviceIndex].set("HasGenericTax", false);
            allServices[serviceIndex] = allServices[serviceIndex].set("Price", 0);
            store.PatientHistoryBillableServicesModels[request.PatientId] = allServices;

            this.CalculateSubtotalAndTotal(request.PatientId, store, store.PatientHistoryBillableServicesModels[request.PatientId]);

            store.EmitChangeToListeners();
        }
    }

    private OnPriceChange(request: LocalEvent, store: PatientStore): void {
        var id = request.Get<string>("id");
        var value = request.Get<number>("value");
        var billableService = store.PatientHistoryBillableServicesModels[request.PatientId];

        for (var i = 0; i < billableService.length; i++) {
            var x = billableService[i];

            if (x.get("Id") === id) {
                billableService[i] = billableService[i].set("Price", value);
            }
        }
        store.PatientHistoryBillableServicesModels[request.PatientId] = billableService;

        this.CalculateSubtotalAndTotal(request.PatientId, store, billableService);
    }

    private OnQuantityChange(request: LocalEvent, store: PatientStore): void {
        var id = request.Get<string>("id");
        var stringValue = request.Get<string>("value");

        var billableService = store.PatientHistoryBillableServicesModels[request.PatientId];

        for (var i = 0; i < billableService.length; i++) {
            var x = billableService[i];

            if (x.get("Id") === id) {
                billableService[i] = billableService[i].set("Qty", Number(stringValue));
            }
        }

        store.PatientHistoryBillableServicesModels[request.PatientId] = billableService;
        this.CalculateSubtotalAndTotal(request.PatientId, store, billableService);
    }

    private SelectBillableService(request: LocalEvent, store: PatientStore): void {
        const allServices = store.PatientHistoryBillableServicesModels[request.PatientId];
        delete store.PatientHistoryBillableServicesModels[request.PatientId];

        const item = request.Get<BillableServiceDropdownItem>("value");
        const id = request.Get<string>("id");

        function indexOf(services: IBillableService[], id: string): number {
            const service = services.firstOrDefault(p => p.get("Id") === id);
            return service ? services.indexOf(service) : -1;
        }

        const serviceIndex = indexOf(allServices, id);

        if (serviceIndex >= 0) {
            allServices[serviceIndex] = allServices[serviceIndex].set("SelectedService", item);
            allServices[serviceIndex] = allServices[serviceIndex].set("HasGST", item.Service.HasGST);
            allServices[serviceIndex] = allServices[serviceIndex].set("HasQST", item.Service.HasQST);
            allServices[serviceIndex] = allServices[serviceIndex].set("HasHST", item.Service.HasHST);
            allServices[serviceIndex] = allServices[serviceIndex].set("HasGenericTax", item.Service.HasGenericTax);
            allServices[serviceIndex] = allServices[serviceIndex].set("Price", item.Service.Price);
            allServices[serviceIndex] = allServices[serviceIndex].set("ServiceNumber", item.Service.Number);
            allServices[serviceIndex] = allServices[serviceIndex].set("NameFr", item.Service.NameFr);
            allServices[serviceIndex] = allServices[serviceIndex].set("NameEn", item.Service.NameEn);
            allServices[serviceIndex] = allServices[serviceIndex].set("NameEs", item.Service.NameEs);
            allServices[serviceIndex] = allServices[serviceIndex].set("CostCenterId", item.Service.CostCenterId);
            allServices[serviceIndex] = allServices[serviceIndex].set("IsFromServiceList", item.Service.IsFromServiceList);

            store.PatientHistoryBillableServicesModels[request.PatientId] = allServices;

            const billableService = store.PatientHistoryBillableServicesModels[request.PatientId];

            if (allServices[allServices.length - 1].get("SelectedService") !== null) {
                const emptyItem = {
                    Id: GuidHelper.NewGuid(),
                    Qty: 1,
                    SelectedService: null,
                    Services: null,
                    Price: 0.00,
                    HasGST: false,
                    HasQST: false,
                    HasHST: false,
                    HasGenericTax: false,
                    IsFromServiceList: false
                };

                const pushItem: IBillableService = Immutable.fromJS(emptyItem);
                billableService.push(pushItem);
                store.PatientHistoryBillableServicesModels[request.PatientId] = billableService;
            }

            this.CalculateSubtotalAndTotal(request.PatientId, store, allServices);

            store.EmitChangeToListeners();
        }
    }

    private CloseCancelPopup(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];

        privateBillModel = privateBillModel.set("CancelPopupIsOpen", false);

        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;

        sessionStore.hideGlobalOverlay();
    }

    private OnCancelClick(request: LocalEvent, store: PatientStore): void {
        let privateBillModel = store.PatientHistoryBillsModels[request.PatientId];

        privateBillModel = privateBillModel.set("CancelPopupIsOpen", true);

        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;

        sessionStore.showGlobalOverlay(true);
    }

    private CancelNewBill(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];

        if (privateBillModel.get("PrivateBillList").size > 0 && privateBillModel.get("IsNewBill")) {
            let bill = privateBillModel.get("PrivateBillList").toArray();
            request.AddItem("privateBill", bill[0]);
            this.ShowSelectedPrivateBill(request, store);

            return;
        }

        // THis here is legacy code for the render of either the Edit or View mode of a Bill
        // When set to true, it will render the page edit/view
        // When set to false, it will render an empty div
        // THIS SHOULD BE CHANGED WHEN REFACTOR ARE NECESSARY
        privateBillModel = privateBillModel.set("ShowDetailsOrNewBill", !privateBillModel.get("IsNewBill"));

        privateBillModel = privateBillModel.set("IsEditBill", false);
        privateBillModel = privateBillModel.set("IsNewBill", false);
        privateBillModel = privateBillModel.set("SelectedDateOfVisit", null);
        privateBillModel = privateBillModel.set("SelectedClinicians", Immutable.fromJS([]));
        privateBillModel = privateBillModel.set("CancelPopupIsOpen", false);
        privateBillModel = privateBillModel.set("SelectedBillBilledTo", PrivateBill_BilledTo.Patient);
        privateBillModel = privateBillModel.set("SelectedBillRecipientDetails", new RecipientDetailsModel());
        privateBillModel = privateBillModel.set("NewBillDiscount", 0);

        store.PatientHistoryBillableServicesModels[request.PatientId] = [];

        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;

        sessionStore.hideGlobalOverlay();
    }

    private ChangeHasQST(request: LocalEvent, store: PatientStore): void {
        var id = request.Get<string>("id");

        var billableService = store.PatientHistoryBillableServicesModels[request.PatientId];

        for (var i = 0; i < billableService.length; i++) {
            var x = billableService[i];

            if (x.get("Id") === id) {
                var isChecked = x.get("HasQST");
                billableService[i] = billableService[i].set("HasQST", !isChecked);
            }
        }

        store.PatientHistoryBillableServicesModels[request.PatientId] = billableService;

        this.CalculateSubtotalAndTotal(request.PatientId, store, store.PatientHistoryBillableServicesModels[request.PatientId]);
    }

    private ChangeHasHST(request: LocalEvent, store: PatientStore): void {
        const id = request.Get<string>("id");

        const billableService = store.PatientHistoryBillableServicesModels[request.PatientId];

        for (let i = 0; i < billableService.length; i++) {
            const x = billableService[i];

            if (x.get("Id") === id) {
                var isChecked = x.get("HasHST");
                billableService[i] = billableService[i].set("HasHST", !isChecked);
            }
        }

        store.PatientHistoryBillableServicesModels[request.PatientId] = billableService;

        this.CalculateSubtotalAndTotal(request.PatientId, store, store.PatientHistoryBillableServicesModels[request.PatientId]);
    }

    private ChangeHasGenericTax(request: LocalEvent, store: PatientStore): void {
        const id = request.Get<string>("id");

        const billableService = store.PatientHistoryBillableServicesModels[request.PatientId];

        for (let i = 0; i < billableService.length; i++) {
            const x = billableService[i];

            if (x.get("Id") === id) {
                var isChecked = x.get("HasGenericTax");
                billableService[i] = billableService[i].set("HasGenericTax", !isChecked);
            }
        }

        store.PatientHistoryBillableServicesModels[request.PatientId] = billableService;

        this.CalculateSubtotalAndTotal(request.PatientId, store, store.PatientHistoryBillableServicesModels[request.PatientId]);
    }

    private ChangeHasGST(request: LocalEvent, store: PatientStore): void {
        var id = request.Get<string>("id");

        var billableService = store.PatientHistoryBillableServicesModels[request.PatientId];

        for (var i = 0; i < billableService.length; i++) {
            var x = billableService[i];

            if (x.get("Id") === id) {
                var isChecked = x.get("HasGST");
                billableService[i] = billableService[i].set("HasGST", !isChecked);
            }
        }

        store.PatientHistoryBillableServicesModels[request.PatientId] = billableService;

        this.CalculateSubtotalAndTotal(request.PatientId, store, store.PatientHistoryBillableServicesModels[request.PatientId]);
    }

    private RemoveService(request: LocalEvent, store: PatientStore): void {
        var billableService = store.PatientHistoryBillableServicesModels[request.PatientId];

        if (billableService.length > 1)
            billableService = billableService.filter(x => x.get("Id") !== request.Get<string>("id"));

        store.PatientHistoryBillableServicesModels[request.PatientId] = billableService;

        this.CalculateSubtotalAndTotal(request.PatientId, store, store.PatientHistoryBillableServicesModels[request.PatientId]);
    }

    private AddNewEmptyService(request: LocalEvent, store: PatientStore): void {
        var billableService = store.PatientHistoryBillableServicesModels[request.PatientId];

        var item = {
            Id: GuidHelper.NewGuid(),
            Qty: 1,
            SelectedService: null,
            Services: null,
            Price: 0.00,
            HasGST: false,
            HasQST: false,
            HasHST: false,
            HasGenericTax: false,
            IsFromServiceList: false
        };

        var pushItem: IBillableService = Immutable.fromJS(item);
        billableService.push(pushItem);
        store.PatientHistoryBillableServicesModels[request.PatientId] = billableService;
    }

    private LoadDummyServices(request: LocalEvent, store: PatientStore): void {
        var newId = GuidHelper.NewGuid();
        var itemA: IBillableService = Immutable.fromJS({
            Id: newId,
            Qty: 1,
            SelectedService: null,
            Services: null,
            Price: 0.00,
            HasGST: false,
            HasQST: false,
            HasHST: false,
            HasGenericTax: false,
            IsFromServiceList: false
        });

        store.PatientHistoryBillableServicesModels[request.PatientId] = [itemA];
    }

    private OnSiteChanged(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        const allServices = store.PatientHistoryBillableServicesModels[request.PatientId];

        privateBillModel = privateBillModel.set("SelectedSiteId", request.Get<Guid>("value"));

        var site = cacheStore.GetSiteById(request.Get<string>("value"));
        var allSites = privateBillModel.get("Sites").toArray();

        privateBillModel = privateBillModel.set("Clinicians", CommonHelper.GetEmployeesBasedOnSite(Immutable.List(cacheStore.GetClinicians(false)), allSites, site.Model.Id));
        privateBillModel = privateBillModel.set("SelectedClinicianName", "");
        privateBillModel = privateBillModel.set("SelectedClinicians", null);

        privateBillModel = privateBillModel.set("GSTPercent", site.Model.GstValue);
        privateBillModel = privateBillModel.set("QSTPercent", site.Model.QstValue);
        privateBillModel = privateBillModel.set("HSTPercent", site.Model.HstValue);
        privateBillModel = privateBillModel.set("GenericTaxPercent", site.Model.GenericTaxValue);
        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;

        if (sessionStore.getCurrentEmployee().isActiveSiteMexico() === true) {
            store.ActionsHistory.LoadCostCenterForMexico(request.Get<string>("value"));
        }
        this.CalculateSubtotalAndTotal(request.PatientId, store, allServices);
    }

    private OnTodayClick(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];

        privateBillModel = privateBillModel.set("SelectedDateOfVisit", moment().toDate());

        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
    }

    public CreateNewBill(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        privateBillModel = privateBillModel.set("CancelPopupIsOpen", false);

        var selectedSite: SitesModel = null;

        if (cacheStore.SiteCache.length > 1) {
            var a: SiteUIModel[] = [];
            for (let cachedSite of cacheStore.SiteCache) {
                sessionStore.getCurrentEmployee().Model.AssociatedSites.forEach(h => {
                    if (h === cachedSite.Model.Id) {
                        var site = SiteUIModel.FromCompositeObject(cachedSite);
                        a.push(site);
                    }
                });
            }

            var sites = Immutable.List(a);
            privateBillModel = privateBillModel.set("Sites", sites);

            if (!IsNullOrWhiteSpace(sessionStore.cachedPrivateBillSiteId)) {
                selectedSite = cacheStore.SiteCache.filter(s => s.Model.Id === sessionStore.cachedPrivateBillSiteId)[0];
                privateBillModel = privateBillModel.set("SelectedSiteId", sessionStore.cachedPrivateBillSiteId);
                privateBillModel = privateBillModel.set("Clinicians", CommonHelper.GetEmployeesBasedOnSite(Immutable.List(cacheStore.GetClinicians(false)), a, sessionStore.cachedPrivateBillSiteId));
            }
            else {
                selectedSite = cacheStore.SiteCache.filter(s => s.Model.Id === sessionStore.getCurrentEmployee().getActiveSite().Model.Id)[0];
                privateBillModel = privateBillModel.set("SelectedSiteId", sessionStore.getCurrentEmployee().getActiveSite().Model.Id);
                privateBillModel = privateBillModel.set("Clinicians", CommonHelper.GetEmployeesBasedOnSite(Immutable.List(cacheStore.GetClinicians(false)), a, sessionStore.getCurrentEmployee().getActiveSite().Model.Id));
            }
        }
        else {
            privateBillModel = privateBillModel.set("Clinicians", Immutable.List(cacheStore.GetClinicians(false)));
            selectedSite = sessionStore.getCurrentEmployee().getActiveSite();
            privateBillModel = privateBillModel.set("SelectedSiteId", selectedSite.Model.Id);
        }

        if (sessionStore.getCurrentEmployee().isActiveSiteMexico() === true) {
            store.ActionsHistory.LoadCostCenterForMexico(selectedSite.Model.Id);
        }

        privateBillModel = privateBillModel
            .set("GSTPercent", selectedSite.Model.GstValue)
            .set("QSTPercent", selectedSite.Model.QstValue)
            .set("HSTPercent", selectedSite.Model.HstValue)
            .set("GenericTaxPercent", selectedSite.Model.GenericTaxValue)
            .set("SelectedDateOfVisit", DateTime.nowDeprecated())
            .set("SelectedPaymentMethod", BillingPaymentMethod.Payable)
            .set("NewBillDiscount", 0)
            .set("NewBillTotal", 0)
            .set("NewBillSubtotal", 0)
            .set("NewBillQSTTotal", 0)
            .set("NewBillGSTTotal", 0)
            .set("NewBillHSTTotal", 0)
            .set("NewBillGenericTaxTotal", 0)
            .set("NewBillTotalAmount", 0)
            .set("NewBillNotes", null)
            .set("BillNumber", "")
            .set("Notes", "")
            .set("SelectedBillRecipientDetails", new RecipientDetailsModel())
            .set("SelectedBillBilledTo", PrivateBill_BilledTo.Patient)
            .set("PrivateBillId", null);

        if (sessionStore.rememberPrivateBillProvider) {
            var ps = privateBillModel.get("Clinicians")
                .toArray()
                .filter((c: Person) => c.ContactFacetId === sessionStore.privateBillProviderContactFacetId);
            if (ps.length > 0) {
                privateBillModel = privateBillModel.set("SelectedClinicians", ps.toImmutable());
            }

            privateBillModel = privateBillModel.set("IsProviderLocked", true);
        }
        else {
            if (
                sessionStore.myleEmployeeType === EmployeeType.Practician
                && Employee.getTitle(sessionStore.getCurrentEmployee().Model) != EmployeeTitle.MedicalOfficeAssistant
            ) {
                privateBillModel = privateBillModel.set("SelectedClinicians", Immutable.List([Person.FromEmployee(sessionStore.getCurrentEmployee().Model)]));
                privateBillModel = privateBillModel.set("SelectedClinicianName", sessionStore.getCurrentEmployee().Name);
            }
            else {
                privateBillModel = privateBillModel.set("SelectedClinicians", null);
            }

            privateBillModel = privateBillModel.set("IsProviderLocked", false);
        }

        privateBillModel = privateBillModel.set("PortalLinkedItemModel", Immutable.fromJS(PortalHelper.getDefaultPortalLinkedItemModel()));

        if (sessionStore.getCurrentEmployee().isActiveSiteMexico() === false) {
            privateBillModel = privateBillModel.set("ShowDetailsOrNewBill", true);
            privateBillModel = privateBillModel.set("IsNewBill", true);
        }

        privateBillModel = privateBillModel.set("IsEditBill", false);

        var itemA = {
            Id: GuidHelper.NewGuid(),
            Qty: 1,
            SelectedService: null,
            Services: null,
            Price: 0.00,
            HasGST: false,
            HasQST: false,
            HasHST: false,
            HasGenericTax: false,
            IsFromServiceList: false
        };

        if (!IsNullOrWhiteSpace(sessionStore.cachedPrivateBillCostCenterId)) { privateBillModel = privateBillModel.set("SelectedCostCenterId", sessionStore.cachedPrivateBillCostCenterId); }
        else { privateBillModel = privateBillModel.set("SelectedCostCenterId", ""); }

        store.PatientHistoryBillableServicesModels[request.PatientId] = [Immutable.fromJS(itemA)];

        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
    }

    private DateOfVisitChange(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        privateBillModel = privateBillModel.set("SelectedDateOfVisit", request.Get<Date>("value"));

        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
    }

    private PortalStatusClicked(request: LocalEvent, store: PatientStore): void {
        var bill = request.Get<PatientPrivateBillItem>("bill");
        if (bill == null)
            return;

        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        var portalStatusModel = privateBillModel.get("PortalLinkedItemModel");

        if (privateBillModel.get("IsClosed") === false) {
            portalStatusModel = portalStatusModel.set("ExtraButtonText", cacheStore.GetString("SyncAndPrintLabel"));
            portalStatusModel = portalStatusModel.set("ExtraButtonOperation", LocalPatientOperations.History.PrivateBill_PortalStatusSaveAndPrintFromEdit);
            portalStatusModel = portalStatusModel.set("SendButtonText", cacheStore.GetString("SyncAndCloseLabel"));
        }
        else if (bill.IsClosed === false) {
            portalStatusModel = portalStatusModel.set("ExtraButtonText", cacheStore.GetString("SyncAndPrintLabel"));
            portalStatusModel = portalStatusModel.set("ExtraButtonOperation", LocalPatientOperations.History.PrivateBill_SendBillToPortal);
            portalStatusModel = portalStatusModel.set("SendButtonText", cacheStore.GetString("SyncAndCloseLabel"));

            privateBillModel = privateBillModel.set("BillFromListForSend", bill);
        }
        else {
            portalStatusModel = portalStatusModel.set("SendButtonText", cacheStore.GetString("SendLabel"));
        }

        portalStatusModel = portalStatusModel.set("DefaultSubject", bill.getDefaultSubject());
        portalStatusModel = portalStatusModel.set("LinkedItemModel", bill);
        portalStatusModel = portalStatusModel.set("ErrorMessage", null);

        portalStatusModel = portalStatusModel.set("SharedItem", bill.SharedItem);

        let patientModel = store.PatientModels[request.PatientId];
        if (patientModel && patientModel.IndividualResponse) {
            portalStatusModel = portalStatusModel.set("RegistrationStatus", patientModel.IndividualResponse.PortalSyncState);
            portalStatusModel = portalStatusModel.set("PatientContactId", patientModel.IndividualResponse.Contact_id);
        }

        privateBillModel = privateBillModel.set("SelectedPrivateBillId", bill.PrivateBillId);

        if (bill.SharedItem && !IsNullOrWhiteSpace(bill.SharedItem.SharedItemId)) {
            privateBillModel = privateBillModel.set("PortalLinkedItemModel", portalStatusModel);
            store.ActionsHistory.LoadPortalSharedItem(bill.SharedItem.SharedItemId, PortalLinkedItemType.PrivateBill);
        }
        else { // NeverSent
            portalStatusModel = portalStatusModel.set("LinkedItemMessages", Immutable.List([]));
            portalStatusModel = portalStatusModel.set("LinkedItemType", PortalLinkedItemType.PrivateBill);
            portalStatusModel = portalStatusModel.set("IsSending", false);

            privateBillModel = privateBillModel.set("PortalLinkedItemModel", portalStatusModel);
            privateBillModel = privateBillModel.set("IsPortalSyncPopupOpen", true);
            sessionStore.showGlobalOverlay(true);
        }
        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
    }

    private PortalStatusCancel(request: LocalEvent, store: PatientStore): void {
        // set clicked on bill as portal status model, and call action to get portal status from backend
        let privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        privateBillModel = privateBillModel.set("IsPortalSyncPopupOpen", false);

        let portalLinkedItemModel = privateBillModel.get("PortalLinkedItemModel");
        portalLinkedItemModel = PatientHelper.clearPortalLinkItemStates(portalLinkedItemModel);
        privateBillModel = privateBillModel.set("PortalLinkedItemModel", portalLinkedItemModel);

        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
        sessionStore.hideGlobalOverlay();
    }

    private privateBillPortalStatusRevoked(request: LocalEvent, store: PatientStore): void {
        let model = store.PatientHistoryBillsModels[request.PatientId];
        let portalStatusModel = model.get("PortalLinkedItemModel");
        let sharedItemId = portalStatusModel.get("LinkedItemModel").getSharedItemId();
        let bill = portalStatusModel.get("LinkedItemModel") as PatientPrivateBillItem;
        let billId = bill.PrivateBillId;

        if (!IsNullOrWhiteSpace(sharedItemId)) {
            let messageIdsToRevoke = PortalHelper.getSharedMessageIdsToRevoke(portalStatusModel);
            store.ActionsHistory.revokeHistorySharedItem(sharedItemId, messageIdsToRevoke, sessionStore.userId, PortalLinkedItemType.PrivateBill, billId, true);
        }
    }

    private PortalStatusSaveFromEdit(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        var portalStatusModel = privateBillModel.get("PortalLinkedItemModel");
        portalStatusModel = portalStatusModel.set("NewMessageText", request.Get<string>("messageText"));
        privateBillModel = privateBillModel.set("PortalLinkedItemModel", portalStatusModel);
        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
        this.Save(request, store, true, true);
    }

    private PortalStatusSaveAndPrintFromEdit(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        var portalStatusModel = privateBillModel.get("PortalLinkedItemModel");
        portalStatusModel = portalStatusModel.set("NewMessageText", request.Get<string>("messageText"));
        privateBillModel = privateBillModel.set("PortalLinkedItemModel", portalStatusModel);

        if (privateBillModel.get("IsNewBill") === false) {
            privateBillModel = privateBillModel.set("IsEditBill", true);
        }

        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
        this.SaveAndPrint(request, store, true, true);
    }

    private DeleteSelectedBill(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        store.ActionsHistory.DeleteSelectedBill(privateBillModel.get("SelectedPrivateBillId"));
    }

    private closeSelectedBillImpl(request: LocalEvent, store: PatientStore, shouldPrint: boolean = false): void {
        const privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        const privateBillId = privateBillModel.get("SelectedPrivateBillId");

        // Close bill
        if (!privateBillModel.get("IsClosed"))
            store.ActionsHistory.CloseSelectedBill(privateBillId);

        if (!shouldPrint)
            return;

        // Print
        window.setTimeout(() => store.ActionsHistory.PrintPrivateBill(privateBillId), 200);
    }

    private sendBillToPortalImpl(request: LocalEvent, store: PatientStore, shouldPrint: boolean = false): void {
        if (!cacheStore.ClinicSettings.EnablePrivateBillRecipient) {
            this.closeSelectedBillImpl(request, store, shouldPrint);
        }
        else if (cacheStore.ClinicSettings.EnablePrivateBillRecipient && shouldPrint) {
            window.setTimeout(() => store.ActionsHistory.PrintPrivateBill(privateBillModel.get("SelectedPrivateBillId")), 200);
        }

        let privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        let portalStatusModel = privateBillModel.get("PortalLinkedItemModel");

        let subject = request.Get<string>("subject");
        let messageText = request.Get<string>("messageText");
        let includeDocument = request.Get<boolean>("includeDocument");
        const priority = request.Get<SharedItemPriority>("priority");
        const canPatientReplyWithMessage = request.Get<boolean>("canPatientReplyWithMessage");

        let portalMessages: SharedItemMessage[] = [];
        if (portalStatusModel != null) {
            portalMessages = PatientHelper.getShareItemMessagesToRevokeOrToSendToPortal(portalStatusModel);
        }

        let hasTextMessage = !IsNullOrWhiteSpace(messageText);
        let sharedItemId = portalStatusModel.get("LinkedItemModel").getSharedItemId();

        // create new message if user has entered text in message textbox
        if (hasTextMessage) {
            portalMessages.push(SharedItemMessage.createNewSharedItemMessage(messageText, priority));
        }

        let patient = store.PatientModels[request.PatientId];
        // Send bill to portal
        store.ActionsHistory.sendHistorySharedItemToPortal(
            privateBillModel.get("SelectedPrivateBillId"),
            sharedItemId,
            subject,
            PortalLinkedItemType.PrivateBill,
            sessionStore.getCurrentEmployee().ContactId,
            patient.IndividualResponse.Contact_id,
            sessionStore.userId,
            portalMessages,
            includeDocument,
            portalStatusModel.get("LinkedItemModel").hasBeenModifiedSinceLastShared(),
            canPatientReplyWithMessage
        );

        portalStatusModel = portalStatusModel.set("IsSending", true);
        privateBillModel = privateBillModel.set("IsEditBill", false);
        privateBillModel = privateBillModel.set("PortalLinkedItemModel", portalStatusModel);

        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
    }

    private closeSelectedBill(request: LocalEvent, store: PatientStore): void {
        this.closeSelectedBillImpl(request, store, false);
    }

    private closeSelectedBillThenPrint(request: LocalEvent, store: PatientStore): void {
        this.closeSelectedBillImpl(request, store, true);
    }

    private sendBillToPortal(request: LocalEvent, store: PatientStore): void {
        this.sendBillToPortalImpl(request, store, false);
    }

    private sendBillToPortalThenPrint(request: LocalEvent, store: PatientStore): void {
        this.sendBillToPortalImpl(request, store, true);
    }

    private OnModifyOrDuplicateClick(request: LocalEvent, store: PatientStore): void {
        const isNewBill = request.Get<boolean>("isNewBill");

        let privateBillModel = store.PatientHistoryBillsModels[request.PatientId];

        if (cacheStore.SiteCache.length > 1) {
            var a: SiteUIModel[] = [];
            const previousSiteId = privateBillModel.get("SelectedBillSiteId");
            const currentEmployeeSites = sessionStore.getCurrentEmployee().Model.AssociatedSites;
            for (let cachedSite of cacheStore.SiteCache) {
                if (previousSiteId == cachedSite.Model.Id
                    || currentEmployeeSites.any(s => s == cachedSite.Model.Id)) {
                    const site = SiteUIModel.FromCompositeObject(cachedSite);
                    a.push(site);
                }
            }

            var sites = Immutable.List(a);
            privateBillModel = privateBillModel.set("Sites", sites);

            privateBillModel = privateBillModel.set("Clinicians", CommonHelper.GetEmployeesBasedOnSite(Immutable.List(cacheStore.GetClinicians(false)), sites.toArray(), privateBillModel.get("SelectedBillSiteId")));
        }
        else {
            privateBillModel = privateBillModel.set("Clinicians", Immutable.List(cacheStore.GetClinicians(false)));
            privateBillModel = privateBillModel.set("SelectedSiteId", privateBillModel.get("SelectedBillSiteId"));
        }

        privateBillModel = privateBillModel.set("SelectedSiteId", privateBillModel.get("SelectedBillSiteId"));

        var ps = privateBillModel.get("Clinicians").toArray().filter((c: Person) => c.ContactFacetId === privateBillModel.get("EmployeeContactFacetId"));
        privateBillModel = privateBillModel.set("SelectedClinicians", ps.toImmutable());

        var employee = cacheStore.GetEmployeeByContactFacetId(privateBillModel.get("EmployeeContactFacetId"));
        if (!!employee) {
            const employeeTitle = cacheStore.GetLookupById(employee?.PrefixLookupId);
            privateBillModel = privateBillModel.set("SelectedClinicianName", employeeTitle.GetValue() + " " + employee.LastName + ", " + employee.FirstName);
        }

        var site = cacheStore.SiteCache.filter(n => n.Model.Id === privateBillModel.get("SelectedBillSiteId"));
        privateBillModel = privateBillModel.set("GSTPercent", site[0].Model.GstValue);
        privateBillModel = privateBillModel.set("QSTPercent", site[0].Model.QstValue);
        privateBillModel = privateBillModel.set("HSTPercent", site[0].Model.HstValue);

        if (!isNewBill) {
            privateBillModel = privateBillModel.set("NewBillDiscount", privateBillModel.get("Discount"));
            privateBillModel = privateBillModel.set("IsCashSelected", privateBillModel.get("DiscountType") === "$");
        }

        privateBillModel = privateBillModel.set("GenericTaxPercent", site[0].Model.GenericTaxValue);

        privateBillModel = privateBillModel.set("DateOfVisit", privateBillModel.get("BillDateRaw"));

        const paymentMethodId = BillingPaymentMethodsHelper.getPaymentMethodIdWithValue(privateBillModel.get("PaymentMethod"));
        privateBillModel = privateBillModel.set("SelectedPaymentMethod", paymentMethodId);

        var billedService = privateBillModel.get("BilledService").toArray();
        var newBillArray = [];

        billedService.map(n => {
            var service = new BillableServiceDropdownItem();
            service.Service.BillableServiceId = n.ServiceNumber != "N/A" ? GuidHelper.NewGuid() : GuidHelper.Empty();
            service.Service.Price = n.ServicePrice;
            service.Service.HasGST = n.HasGST == true;
            service.Service.HasQST = n.HasQST == true;
            service.Service.HasHST = n.HasHST == true;
            service.Service.HasGenericTax = n.HasGenericTax == true;
            service.Service.IsFromServiceList = n.ServiceNumber != "N/A" ? true : false;
            service.Service.NameEn = n.ServiceName;
            service.Service.NameFr = n.ServiceName;
            service.Service.NameEs = n.ServiceName;
            service.Service.Number = n.ServiceNumber;
            service.Service.CostCenterId = n.CostCenterId;

            var item = {
                Id: GuidHelper.NewGuid(),
                Qty: n.ServiceMultiplicator,
                SelectedService: service,
                Services: null,
                Price: n.ServicePrice,
                HasGST: n.HasGST,
                HasQST: n.HasQST,
                HasHST: n.HasHST,
                HasGenericTax: n.HasGenericTax,
                GST: n.GST,
                QST: n.QST,
                HST: n.HST,
                GenericTax: n.GenericTax,
                IsFromServiceList: n.ServiceNumber != "N/A" ? true : false,
                ServiceNumber: n.ServiceNumber,
                NameFr: n.ServiceName,
                NameEn: n.ServiceName,
                NameEs: n.ServiceName
            };

            var pushItem: IBillableService = Immutable.fromJS(item);
            newBillArray.push(pushItem);
        });

        var emptyItem = {
            Id: GuidHelper.NewGuid(),
            Qty: 1,
            SelectedService: null,
            Services: null,
            Price: 0.00,
            HasGST: false,
            HasQST: false,
            HasHST: false,
            HasGenericTax: false,
            IsFromServiceList: false
        };

        var pushEmptyItem: IBillableService = Immutable.fromJS(emptyItem);
        newBillArray.push(pushEmptyItem);

        privateBillModel = privateBillModel.set("IsEditBill", !isNewBill);
        privateBillModel = privateBillModel.set("IsNewBill", isNewBill);
        privateBillModel = privateBillModel.set("ShowDetailsOrNewBill", true);
        privateBillModel = privateBillModel.set("CancelPopupIsOpen", false);
        privateBillModel = privateBillModel.set("SelectedDateOfVisit", new Date(privateBillModel.get("DateOfVisit")));
        privateBillModel = privateBillModel.set("SelectedBillBilledTo", privateBillModel.get("BilledTo"));
        privateBillModel = privateBillModel.set("SelectedBillRecipientDetails", privateBillModel.get("RecipientDetails"));
        privateBillModel = privateBillModel.set("NewBillNotes", privateBillModel.get("Notes"));

        if (isNewBill)
            privateBillModel = privateBillModel.set("BillNumber", "");
        store.PatientHistoryBillableServicesModels[request.PatientId] = newBillArray;
        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;

        if (sessionStore.getCurrentEmployee().isActiveSiteMexico() === true) {
            store.ActionsHistory.LoadCostCenterForMexico(privateBillModel.get("SelectedBillSiteId"));
        }

        this.CalculateSubtotalAndTotal(request.PatientId, store, newBillArray);
    }

    private LockProvider(request: LocalEvent, store: PatientStore): void {
        var privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        privateBillModel = privateBillModel.set("IsProviderLocked", !privateBillModel.get("IsProviderLocked"));

        sessionStore.rememberPrivateBillProvider = privateBillModel.get("IsProviderLocked");
        sessionStore.privateBillProviderContactFacetId = privateBillModel.get("IsProviderLocked") ? privateBillModel.get("SelectedClinicians").toArray()[0].ContactFacetId : "";

        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
    }

    private onPortalInvoiceMessageChecked(request: LocalEvent, store: PatientStore): void {
        var checkedItem = request.Get<SharedItemMessage>("item");

        if (checkedItem != null) {
            let privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
            let model = privateBillModel.get("PortalLinkedItemModel");

            model = PatientHelper.handlePortalItemCheck(model, checkedItem);

            privateBillModel = privateBillModel.set("PortalLinkedItemModel", model);

            store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
        }
    }

    private onRecipientDetailsChanged(request: LocalEvent, store: PatientStore): void {
        const recipientDetails = request.Get<RecipientDetailsModel>("recipientDetails");
        let privateBillModel = store.PatientHistoryBillsModels[request.PatientId];

        privateBillModel = privateBillModel.set("SelectedBillRecipientDetails", recipientDetails);
        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
    }

    private openMarkBillAsPaidPopup(request: LocalEvent, store: PatientStore): void {
        store.PatientHistoryBillsModels[request.PatientId] = store.PatientHistoryBillsModels[request.PatientId]
            .set("IsMarkAsPaidPopupOpen", true);
    }

    private closeMarkBillAsPaidPopup(request: LocalEvent, store: PatientStore): void {
        store.PatientHistoryBillsModels[request.PatientId] = store.PatientHistoryBillsModels[request.PatientId]
            .set("IsMarkAsPaidPopupOpen", false);
    }

    private updateBillAndCloseMarkBillAsPaidPopup(request: LocalEvent, store: PatientStore): void {
        const paymentType = request.Get<number>("paymentType");
        const paymentDate = request.Get<Date>("paymentDate");
        const canModifyPaymentInformation = request.Get<boolean>("canModifyPaymentInformation");
        const privateBillId = request.Get<string>("privateBillId");
        const lastUpdateDate = request.Get<Date>("lastUpdateDate");

        const paymentMethodLabel = BillingPaymentMethodsHelper.getPaymentMethodLabelWithId(paymentType);

        // update bill in PrivateBillList
        const privateBillList = store.PatientHistoryBillsModels[request.PatientId].get("PrivateBillList").toArray();
        for (const bill of privateBillList) {
            if (bill.Model.PrivateBillId == privateBillId) {
                bill.Model.LastUpdateDate = lastUpdateDate;
                bill.Model.IsClosed = true;
                bill.Model.CanModifyPaymentInformation = canModifyPaymentInformation;
                break;
            }
        }

        store.PatientHistoryBillsModels[request.PatientId] = store.PatientHistoryBillsModels[request.PatientId]
            .set("PaymentMethod", paymentMethodLabel)
            .set("PaymentDate", paymentDate)
            .set("IsMarkAsPaidPopupOpen", false)
            .set("CanModifyPaymentInformation", canModifyPaymentInformation)
            .set("PrivateBillList", Immutable.List(privateBillList));
    }

    private onBilledToChanged(request: LocalEvent, store: PatientStore): void {
        const billedTo = request.Get<PrivateBill_BilledTo>("value");
        let privateBillModel = store.PatientHistoryBillsModels[request.PatientId];

        privateBillModel = privateBillModel.set("SelectedBillBilledTo", billedTo);
        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
    }

    private BeforeSaving(request: LocalEvent, store: PatientStore, IsSave: boolean = false): void {
        const privateBillModel = store.PatientHistoryBillsModels[request.PatientId];

        sessionStore.cachedPrivateBillSiteId = privateBillModel.get("SelectedSiteId");

        const costCenterList = privateBillModel.get("CostCenterList");
        if (costCenterList != null && costCenterList.count() > 0) {
            sessionStore.cachedPrivateBillCostCenterId = privateBillModel.get("SelectedCostCenterId");
        }
        else {
            sessionStore.cachedPrivateBillCostCenterId = "";
        }

        const portalStatusModel = privateBillModel.get("PortalLinkedItemModel")
            .set("NewMessageText", request.Get<string>("messageText"));

        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel
            .set("IsSave", IsSave)
            .set("IsSaveLocked", true)
            .set("PortalLinkedItemModel", portalStatusModel);
    }

    private async OnPrintClick(request: LocalEvent, store: PatientStore): Promise<void> {
        this.BeforeSaving(request, store, true);
        const privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        const billedServices = store.PatientHistoryBillableServicesModels[request.PatientId];

        const response = await store.ActionsPrivateBill.saveAndPrintPrivateBillAsync(request, store, privateBillModel, billedServices);

        if (!response?.isSuccess) {
            sessionStore.showPopup(response.errors);
        }
        else {
            PrintHelper.ShowPrintPreviewFileName(response.data?.FileName);
        }

        store.PatientHistoryBillsModels[request.PatientId] = store.PatientHistoryBillsModels[request.PatientId]
            .set("SelectedPrivateBillId", response.data.PrivateBillId)
            .set("IsNewBill", false)
            .set("IsSaveLocked", false);

        sessionStore.dispatch((LocalPatientOperations.History.PrivateBill_LoadPatientPrivateBill));
    }

    private async OnFaxClick(request: LocalEvent, store: PatientStore): Promise<void> {
        this.BeforeSaving(request, store, true);
        const privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        const billedServices = store.PatientHistoryBillableServicesModels[request.PatientId];

        const response = await store.ActionsPrivateBill.saveAndFaxPrivateBillAsync(request, store, privateBillModel, billedServices);

        if (response.isSuccess) {
            displayToast(MessageToast.Success, cacheStore.GetString("FaxSentLabel"));
        }
        else {
            sessionStore.showPopup(response.errors);
        }

        store.PatientHistoryBillsModels[request.PatientId] = store.PatientHistoryBillsModels[request.PatientId]
            .set("SelectedPrivateBillId", response.data.NewPrivatilleId)
            .set("IsNewBill", false)
            .set("IsSaveLocked", false);

        sessionStore.dispatch((LocalPatientOperations.History.PrivateBill_LoadPatientPrivateBill));
    }

    private async UpsertAndSyncBillToPortal(request: LocalEvent, store: PatientStore): Promise<void> {
        const includeDocument = request.Get<string>("includeDocument");
        const newMessage = request.Get<string>("newMessage");
        const shouldGeneratePDF = request.Get<boolean>("shouldGeneratePDF");
        const subject = request.Get<string>("subject");
        const priority = request.Get<SharedItemPriority>("priority");
        const canPatientReplyWithMessage = request.Get<boolean>("canPatientReplyWithMessage");

        let portalMessages: SharedItemMessage[] = [];

        if (newMessage) {
            portalMessages.push(SharedItemMessage.createNewSharedItemMessage(newMessage, priority));
        }

        let PrivateBill = {};
        let PrivateBillId = patientStore.PatientHistoryBillsModels[request.PatientId].get("PrivateBillId");

        const privateBillModel = patientStore.PatientHistoryBillsModels[request.PatientId];
        const isInEditMode = privateBillModel.get("IsNewBill") || privateBillModel.get("IsEditBill");

        if (!PrivateBillId || isInEditMode) {
            PrivateBill = BuildSavePrivateBillRequestHelper.PrepareRequestData(
                privateBillModel,
                patientStore.PatientModels[request.PatientId].InactivableResponse.Id,
                false,
                sessionStore.getCurrentEmployee().ContactFacetId,
                patientStore.PatientHistoryBillableServicesModels[request.PatientId]
            );
        }

        const response = await Client.postAsync<UpsertAndSyncBillToPortalResponse>(
            ServerOperations.Server.Myle_PrivateBill_UpsertAndSyncPrivateBill,
            {
                PrivateBill,
                PrivateBillId,
                IncludeLinkedItem: includeDocument,
                PatientContactId: request.PatientId,
                Messages: portalMessages,
                Subject: subject,
                ShouldGeneratePDF: shouldGeneratePDF,
                CanPatientReplyWithMessage: canPatientReplyWithMessage
            }
        );

        if (!response?.isSuccess) {
            sessionStore.showPopup(response.errors);
        }

        if (shouldGeneratePDF) {
            window.open(apiSettings.buildAppUrl(`api/appointments/fetch/${response.data?.FileName}`, { auth: true }), "_blank");
        }

        store.PatientHistoryBillsModels[request.PatientId] = store.PatientHistoryBillsModels[request.PatientId]
            .set("SelectedPrivateBillId", response.data.PrivateBillId)
            .set("IsNewBill", false)
            .set("IsSaveLocked", false);

        sessionStore.dispatch((LocalPatientOperations.History.PrivateBill_LoadPatientPrivateBill));
    }

    private closeSendToPortalPopup(request: LocalEvent, store: PatientStore): void {
        const privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel.set("IsPortalSyncPopupOpen", false);
        sessionStore.hideGlobalOverlay();
    }
}

export { PatientHistoryPrivateBillHandler };
